import {observer} from "mobx-react";
import React from "react";
import {
    ContentExportImport,
    ContentFilter,
    ContentInput,
    ContentInputAndFilter
} from "./styled";
import SelectRangeDate from "@dropDesk/presentation/components/dates/select_range_date";
import ButtonNew from "@dropDesk/presentation/components/buttons/button_new";
import {useInjection} from "inversify-react";
import {AppController} from "@dropDesk/presentation/app/app.controller";
import {Tooltip} from "antd";
import {useBetween} from "use-between";
import {
    useModalsReportTicketAggregate
} from "@dropDesk/presentation/pages/reports/report_ticket_init/report_ticket_aggregate/ui/modals/handle_change_visible";
import {PeriodEnum} from "@dropDesk/utils/helpers/common";
import {ICONS_DROPDESK} from "@dropDesk/storage/icons/icons.d";
import {ClientEntity} from "@dropDesk/domain/entities/client/client.entity";
import {UserEntity} from "@dropDesk/domain/entities/user/user.entity";
import {SectorEntity} from "@dropDesk/domain/entities/sector/sector.entity";
import {
    TicketsDescriptionStaticEntity
} from "@dropDesk/domain/entities/ticket_description_static/ticket_description_static.entity";
import ReportEntityFiltersButton from "@dropDesk/presentation/components/filters/report_entity_filters";
import FiltersApplied, {
    AppliedFilterItem,
    FilterKey
} from "@dropDesk/presentation/components/filters/filters_applied";


const SearchBar = observer(
    ({
         loading,
         getReportTicketsAggregate,
         period,
         setPeriod,
         hasDataInGraphics,
         labelPeriodCustom,
         filterClient,
         filterUserClient,
         filterSector,
         filterAttendant,
         filterDescriptionStatic,
         setFilterClient,
         setFilterUserClient,
         setFilterSector,
         setFilterAttendant,
         setFilterDescriptionStatic,
         clearEntityFilters,
     }: {
         loading: boolean,
         getReportTicketsAggregate: () => void
         period: Array<Date> | null;
         hasDataInGraphics: boolean,
         setPeriod: (period: Array<Date> | null, label: PeriodEnum) => void
         labelPeriodCustom: PeriodEnum
         filterClient: ClientEntity | null;
         filterUserClient: UserEntity | null;
         filterSector: SectorEntity | null;
         filterAttendant: UserEntity | null;
         filterDescriptionStatic: TicketsDescriptionStaticEntity | null;
         setFilterClient: (filter: ClientEntity | null) => void;
         setFilterUserClient: (filter: UserEntity | null) => void;
         setFilterSector: (filter: SectorEntity | null) => void;
         setFilterAttendant: (filter: UserEntity | null) => void;
         setFilterDescriptionStatic: (filter: TicketsDescriptionStaticEntity | null) => void;
         clearEntityFilters: () => void;
     }
    ) => {

        const appController = useInjection(AppController);
        const colors = appController.theme.colorScheme;

        const {
            setVisibleModalExportHtml,
            setVisibleModalExport
        } = useBetween(useModalsReportTicketAggregate);

        const appliedFilters: AppliedFilterItem[] = [];

        if (filterClient) {
            appliedFilters.push({
                key: "client",
                label: "Empresa",
                value: filterClient.socialName || filterClient.name,
            });
        }
        if (filterUserClient) {
            appliedFilters.push({
                key: "userClient",
                label: "Contato",
                value: filterUserClient.name,
            });
        }
        if (filterSector) {
            appliedFilters.push({
                key: "sector",
                label: "Setor",
                value: filterSector.realName(appController.user!),
            });
        }
        if (filterAttendant) {
            appliedFilters.push({
                key: "attendant",
                label: "Atendente",
                value: filterAttendant.name,
            });
        }
        if (filterDescriptionStatic) {
            appliedFilters.push({
                key: "descriptionStatic",
                label: "Problema",
                value: filterDescriptionStatic.description,
            });
        }

        const handleRemoveFilter = (key: FilterKey) => {
            switch (key) {
                case "client":
                    setFilterClient(null);
                    break;
                case "userClient":
                    setFilterUserClient(null);
                    break;
                case "sector":
                    setFilterSector(null);
                    break;
                case "attendant":
                    setFilterAttendant(null);
                    break;
                case "descriptionStatic":
                    setFilterDescriptionStatic(null);
                    break;
            }
        };

        return (
            <>
                <ContentFilter background={colors.onBackground}>
                    <ContentInputAndFilter>
                        <ContentInput>
                            <SelectRangeDate
                                size={37}
                                value={period}
                                allowClear={true}
                                onChange={(arrayDate, label) => {
                                    setPeriod(arrayDate, label);
                                    getReportTicketsAggregate();
                                }}
                                labelPeriodCustom={labelPeriodCustom}
                            />
                        </ContentInput>
                        <ReportEntityFiltersButton
                            filterClient={filterClient}
                            filterUserClient={filterUserClient}
                            filterSector={filterSector}
                            filterAttendant={filterAttendant}
                            filterDescriptionStatic={filterDescriptionStatic}
                            setFilterClient={setFilterClient}
                            setFilterUserClient={setFilterUserClient}
                            setFilterSector={setFilterSector}
                            setFilterAttendant={setFilterAttendant}
                            setFilterDescriptionStatic={setFilterDescriptionStatic}
                            loading={loading}
                            onClear={clearEntityFilters}
                        />
                    </ContentInputAndFilter>

                    <ContentExportImport>
                        <Tooltip title={!hasDataInGraphics ? 'Não possui dados' : ''}>
                            <div>
                                <ButtonNew
                                    marginLeft={10}
                                    size={37}
                                    label={'Exportar relatório'}
                                    onClick={() => {
                                        setVisibleModalExportHtml(true);
                                    }}
                                    nameLeftIcon={ICONS_DROPDESK.documentOutline}
                                    sizeLeftIcon={15}
                                    disabled={loading || !hasDataInGraphics}
                                />
                            </div>
                        </Tooltip>
                        {appController.user?.permissionAdmin &&
                            <Tooltip title={!hasDataInGraphics ? 'Não possui dados' : ''}>
                                <div>
                                    <ButtonNew
                                        marginLeft={10}
                                        size={37}
                                        label={'Exportar dados'}
                                        onClick={() => {
                                            setVisibleModalExport(true);
                                        }}
                                        nameLeftIcon={ICONS_DROPDESK.excel}
                                        sizeLeftIcon={15}
                                        disabled={loading || !hasDataInGraphics}
                                    />
                                </div>
                            </Tooltip>}
                    </ContentExportImport>
                </ContentFilter>
                {appliedFilters.length > 0 && (
                    <FiltersApplied
                        items={appliedFilters}
                        onRemoveItem={handleRemoveFilter}
                        onClearAll={clearEntityFilters}
                        paddingWidth={64}
                    />
                )}
            </>
        )
    })
export default SearchBar;
